/**
 * "New brief" CTA — one crisp form.
 *
 * Project + target keyword. Delivery defaults to markdown.
 * Keyword field is the shared KeywordPicker (Phase 1 keyword-picker refactor):
 * typeahead over every keyword in every completed run on the project, plus
 * free-text fallback for keywords the picker doesn't know.
 *
 * When the user picks a real keyword row we forward `input_pins.keyword_id`
 * + `keyword_run_id` in the request body so the worker can enrich the brief
 * with the picked keyword's metadata (v2 pipeline) and downstream tracking
 * / reporting can link back to the source run.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ChevronDown, ChevronRight, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateBriefRequest } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog } from '@/components/ui/form-dialog';
import { FormField, TextAreaField, TextField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { KeywordPicker, type KeywordPickerValue } from '@/components/keyword-picker';
import { summarizeApiError } from '@/lib/api';
import { useCreateBrief } from '@/features/briefs/use-create-brief';

const OptionalPositiveInt = z
  .union([z.literal(''), z.coerce.number().int().positive()])
  .optional();

const FormSchema = z.object({
  project_id: z.string().uuid({ message: 'Pick a project' }),
  target_keyword: z
    .string()
    .trim()
    .min(2, 'What keyword are we writing about?')
    .max(200, 'Too long'),
  business_goal: z.string().max(500, 'Keep under 500 characters').optional(),
  target_position: z
    .union([
      z.literal(''),
      z.coerce.number().int().min(1, '1 or higher').max(100, '100 or lower'),
    ])
    .optional(),
  target_monthly_traffic: z
    .union([z.literal(''), z.coerce.number().int().min(0, '0 or higher')])
    .optional(),
  primary_conversion_action: z
    .string()
    .max(200, 'Keep under 200 characters')
    .optional(),
  review_after_days: OptionalPositiveInt,
});
type FormValues = z.infer<typeof FormSchema>;

interface PreferredSourceRow {
  name: string;
  url: string;
  topic_tags_input: string;
}

function rowsToPreferredSources(
  rows: PreferredSourceRow[],
): { name: string; url: string; topic_tags: string[] }[] {
  return rows
    .map((r) => ({
      name: r.name.trim(),
      url: r.url.trim(),
      topic_tags: r.topic_tags_input
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    }))
    .filter((r) => r.name.length > 0 && r.url.length > 0);
}

interface Project {
  id: string;
  name: string;
  domain?: string;
}

export interface BriefCreateButtonProps {
  projects?: Project[];
  defaultProjectId?: string;
  projectId?: string;
  /** When set (e.g. from competitor analysis), pin it silently. */
  defaultCompetitorAnalysisId?: string;
  defaultKeyword?: string;
  /**
   * Phase 1 keyword-picker — when the caller has the picked-keyword IDs
   * (e.g. brief launched from a keyword-run row), pass them through so the
   * picker starts pre-filled with a `picked` value and the resulting brief
   * inherits `input_pins.keyword_id` + `keyword_run_id`.
   */
  defaultKeywordId?: string;
  defaultKeywordRunId?: string;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  label?: string;
}

export function BriefCreateButton({
  projects,
  defaultProjectId,
  projectId,
  defaultCompetitorAnalysisId,
  defaultKeyword,
  defaultKeywordId,
  defaultKeywordRunId,
  buttonProps,
  label = 'New brief',
}: BriefCreateButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const resolvedProjects: Project[] =
    projects ??
    (projectId ? [{ id: projectId, name: 'Current project' }] : []);
  const resolvedDefault = defaultProjectId ?? projectId;

  return (
    <>
      <Button {...buttonProps} onClick={() => setOpen(true)}>
        <Play className="h-4 w-4" />
        {label}
      </Button>
      {open ? (
        <Dialog
          open={open}
          onOpenChange={setOpen}
          projects={resolvedProjects}
          defaultProjectId={resolvedDefault}
          defaultCompetitorAnalysisId={defaultCompetitorAnalysisId}
          defaultKeyword={defaultKeyword}
          defaultKeywordId={defaultKeywordId}
          defaultKeywordRunId={defaultKeywordRunId}
        />
      ) : null}
    </>
  );
}

function Dialog({
  open,
  onOpenChange,
  projects,
  defaultProjectId,
  defaultCompetitorAnalysisId,
  defaultKeyword,
  defaultKeywordId,
  defaultKeywordRunId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projects: Project[];
  defaultProjectId?: string;
  defaultCompetitorAnalysisId?: string;
  defaultKeyword?: string;
  defaultKeywordId?: string;
  defaultKeywordRunId?: string;
}): JSX.Element {
  const create = useCreateBrief();
  const navigate = useNavigate();

  const initialProjectId =
    defaultProjectId ?? (projects.length === 1 ? projects[0]?.id : '') ?? '';

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      project_id: initialProjectId,
      target_keyword: defaultKeyword ?? '',
      business_goal: '',
      target_position: '',
      target_monthly_traffic: '',
      primary_conversion_action: '',
      review_after_days: '',
    },
    mode: 'onBlur',
  });

  const [preferredSources, setPreferredSources] = useState<PreferredSourceRow[]>(
    [],
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const addSource = (): void => {
    setPreferredSources((prev) => [
      ...prev,
      { name: '', url: '', topic_tags_input: '' },
    ]);
  };
  const updateSource = (
    idx: number,
    patch: Partial<PreferredSourceRow>,
  ): void => {
    setPreferredSources((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };
  const removeSource = (idx: number): void => {
    setPreferredSources((prev) => prev.filter((_, i) => i !== idx));
  };

  const projectId = watch('project_id');
  const targetKeyword = watch('target_keyword');

  // Picker state, kept locally so we can carry keyword_id / keyword_run_id
  // separately from the form's `target_keyword` string. Seeded from the
  // default* props when the caller pre-fills.
  const initialPickerValue: KeywordPickerValue | null = defaultKeyword
    ? defaultKeywordId && defaultKeywordRunId
      ? {
          kind: 'picked',
          keywordId: defaultKeywordId,
          keywordRunId: defaultKeywordRunId,
          term: defaultKeyword,
          volume: null,
          difficulty: null,
          intent: null,
          clusterLabel: null,
        }
      : { kind: 'free-text', term: defaultKeyword }
    : null;
  const [pickerValue, setPickerValue] = useState<KeywordPickerValue | null>(initialPickerValue);

  // Reset picker when the dialog closes / re-opens against a new default.
  useEffect(() => {
    setPickerValue(initialPickerValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultKeyword, defaultKeywordId, defaultKeywordRunId]);

  const submit = handleSubmit(async (values) => {
    // Assemble input_pins from picker selection (Phase 1) and, if the caller
    // opened us from a specific competitor analysis, the pinned analysis id.
    const pins: NonNullable<CreateBriefRequest['input_pins']> = {};
    if (pickerValue?.kind === 'picked') {
      pins.keyword_id = pickerValue.keywordId;
      pins.keyword_run_id = pickerValue.keywordRunId;
    }
    if (defaultCompetitorAnalysisId && targetKeyword === defaultKeyword) {
      pins.competitor_analysis_id = defaultCompetitorAnalysisId;
    }

    const trimmedGoal = values.business_goal?.trim();
    const trimmedConv = values.primary_conversion_action?.trim();
    const cleanedSources = rowsToPreferredSources(preferredSources);
    const personalization =
      cleanedSources.length > 0
        ? { preferred_sources: cleanedSources }
        : undefined;

    const body: CreateBriefRequest = {
      project_id: values.project_id,
      target_keyword: values.target_keyword,
      delivery: 'markdown',
      ...(Object.keys(pins).length > 0 ? { input_pins: pins } : {}),
      ...(personalization ? { personalization } : {}),
      ...(trimmedGoal ? { business_goal: trimmedGoal } : {}),
      ...(typeof values.target_position === 'number'
        ? { target_position: values.target_position }
        : {}),
      ...(typeof values.target_monthly_traffic === 'number'
        ? { target_monthly_traffic: values.target_monthly_traffic }
        : {}),
      ...(trimmedConv ? { primary_conversion_action: trimmedConv } : {}),
      ...(typeof values.review_after_days === 'number'
        ? { review_after_days: values.review_after_days }
        : {}),
    };

    try {
      const brief = await create.mutateAsync(body);
      toast.success('Brief started', {
        description: `We're building a brief for "${values.target_keyword}". This usually takes 2-3 minutes.`,
      });
      reset();
      setPreferredSources([]);
      onOpenChange(false);
      navigate({ to: '/briefs/$briefId', params: { briefId: brief.id } });
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not start the brief',
        fieldLabels: {
          project_id: 'Project',
          target_keyword: 'Target keyword',
          business_goal: 'Business goal',
          target_position: 'Target position',
          target_monthly_traffic: 'Target monthly traffic',
          primary_conversion_action: 'Primary conversion action',
          review_after_days: 'Review after (days)',
        },
      });
      toast.error(summary.title, { description: summary.description });
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={<span />}
      title="Write a brief"
      description="Give us a keyword. We'll ground the brief in competitor evidence and hand you a plan a writer can build from."
      submitLabel="Start brief"
      submitting={isSubmitting || create.isPending}
      onSubmit={submit}
      size="lg"
    >
      <FormField label="Project" error={errors.project_id?.message}>
        {projects.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            You need at least one project first.
          </div>
        ) : (
          <Select
            value={projectId}
            onValueChange={(v) => {
              setValue('project_id', v, { shouldValidate: true });
              if (!defaultKeyword) {
                setValue('target_keyword', '', { shouldValidate: false });
                setPickerValue(null);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pick a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                  {p.domain ? ` — ${p.domain.replace(/^https?:\/\//, '')}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FormField>

      <FormField
        label="Target keyword"
        error={errors.target_keyword?.message}
        hint={
          pickerValue?.kind === 'picked'
            ? 'Picked from keyword research — we\'ll enrich the brief with volume, intent, and cluster context.'
            : 'Search across your keyword research, or type your own.'
        }
      >
        <KeywordPicker
          projectId={projectId || undefined}
          value={pickerValue}
          onChange={(v) => {
            setPickerValue(v);
            setValue('target_keyword', v.term, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          autoFocus={!defaultKeyword}
          disabled={!projectId}
          placeholder="e.g. best domain name generator"
        />
      </FormField>

      <div className="border-t border-border/60 pt-3">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
          className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <span className="flex items-center gap-2">
            {advancedOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Advanced
          </span>
          <span className="text-xs text-muted-foreground/80">
            Business goal, success metrics, preferred sources — optional
          </span>
        </button>

        {advancedOpen ? (
          <div className="mt-3 space-y-4">
            <TextAreaField
              label="Business goal"
              placeholder="e.g. Drive brokerage inquiries for high-value aftermarket domains."
              hint="1-2 sentence commercial goal for this article. Distinct from the numeric success metrics below."
              rows={2}
              maxLength={500}
              error={errors.business_goal?.message}
              {...register('business_goal')}
            />

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FormField
                label="Target position"
                hint="1-100 (SERP rank)"
                error={errors.target_position?.message}
              >
                <Input
                  type="number"
                  min={1}
                  max={100}
                  placeholder="e.g. 3"
                  {...register('target_position')}
                />
              </FormField>
              <FormField
                label="Target monthly traffic"
                hint="Organic visits/month once ranking"
                error={errors.target_monthly_traffic?.message}
              >
                <Input
                  type="number"
                  min={0}
                  placeholder="e.g. 2500"
                  {...register('target_monthly_traffic')}
                />
              </FormField>
              <TextField
                label="Primary conversion action"
                placeholder="e.g. Brokerage inquiry"
                error={errors.primary_conversion_action?.message}
                {...register('primary_conversion_action')}
              />
              <FormField
                label="Review after (days)"
                hint="When to revisit for a refresh"
                error={errors.review_after_days?.message}
              >
                <Input
                  type="number"
                  min={1}
                  placeholder="e.g. 90"
                  {...register('review_after_days')}
                />
              </FormField>
            </div>

            <FormField
              label="Preferred sources (override)"
              hint="Named authorities the prompt can cite. Leave empty to use the project's default sources."
            >
              <div className="space-y-2">
                {preferredSources.map((row, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 gap-2 rounded-md border border-border/70 bg-card p-2 md:grid-cols-[1fr,1fr,1fr,auto]"
                  >
                    <Input
                      placeholder="Name (e.g. ICANN)"
                      value={row.name}
                      onChange={(e) => updateSource(idx, { name: e.target.value })}
                    />
                    <Input
                      placeholder="https://…"
                      value={row.url}
                      onChange={(e) => updateSource(idx, { url: e.target.value })}
                    />
                    <Input
                      placeholder="Topic tags (comma-separated)"
                      value={row.topic_tags_input}
                      onChange={(e) =>
                        updateSource(idx, { topic_tags_input: e.target.value })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove source"
                      onClick={() => removeSource(idx)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={addSource}>
                  Add source
                </Button>
              </div>
            </FormField>
          </div>
        ) : null}
      </div>
    </FormDialog>
  );
}
